// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the words of a mail search, for marking them in the results. Outlook matches the start
// of words ("chalm" finds Chalmar), so a mark covers the typed part at the start of a word.

/** Outlook's search keywords whose value is a person or text worth marking. */
const TEXT_KEYWORDS = new Set([
	'from',
	'to',
	'cc',
	'bcc',
	'subject',
	'body',
	'participants',
	'recipients',
	'attachment',
]);
const OPERATORS = new Set(['and', 'or', 'not']);

/** Lower-case words to mark: `from:willie` gives "willie", `hasattachments:true` and AND/OR give none. */
export function mailSearchWords(query: string): Array<string> {
	const words = new Set<string>();
	for (const raw of query.replace(/["()]/g, ' ').split(/\s+/)) {
		if (!raw || OPERATORS.has(raw.toLowerCase())) continue;
		// keyword:value, or a comparison like received>=2026-10-21.
		const keyword = /^([a-z]+)(:|>=|<=|>|<|=)(.*)$/i.exec(raw);
		const word = keyword
			? keyword[2] === ':' && TEXT_KEYWORDS.has(keyword[1]!.toLowerCase())
				? keyword[3]!
				: ''
			: raw;
		const cleaned = word
			.replace(/^[-+]+/, '')
			.replace(/\*+$/, '')
			.toLowerCase();
		if (cleaned) words.add(cleaned);
	}
	// Longest first, so "chalmar" wins over "chal" where both start at the same place.
	return [...words].sort((a, b) => b.length - a.length);
}

const WORD_CHAR = /[\p{L}\p{N}]/u;

/** [start, end) ranges where a search word starts a word in `text`, in order, never overlapping. */
export function mailSearchMarks(text: string, words: ReadonlyArray<string>): Array<[number, number]> {
	if (!text || words.length === 0) return [];
	const lower = text.toLowerCase();
	const marks: Array<[number, number]> = [];
	for (const word of words) {
		let from = 0;
		for (;;) {
			const at = lower.indexOf(word, from);
			if (at === -1) break;
			from = at + 1;
			// The start of a word only: "inv" marks "invoice" but not "convince".
			if (at > 0 && WORD_CHAR.test(text[at - 1]!) && WORD_CHAR.test(word[0]!)) continue;
			const end = at + word.length;
			if (marks.some(([start, stop]) => at < stop && end > start)) continue;
			marks.push([at, end]);
		}
	}
	return marks.sort((a, b) => a[0] - b[0]);
}

export type MailSearchDate = 'any' | 'week' | 'month' | 'year';

/** The filter chips under the search box. The folder goes as its own parameter, not in the words. */
export interface MailSearchFilters {
	from: {name: string | null; address: string} | null;
	attachments: boolean;
	unread: boolean;
	date: MailSearchDate;
	folder: {id: string; name: string} | null;
}

export const NO_MAIL_SEARCH_FILTERS: MailSearchFilters = {
	from: null,
	attachments: false,
	unread: false,
	date: 'any',
	folder: null,
};

const DATE_DAYS: Record<Exclude<MailSearchDate, 'any'>, number> = {week: 7, month: 30, year: 365};

export function hasMailSearchFilters(filters: MailSearchFilters): boolean {
	return (
		filters.from != null || filters.attachments || filters.unread || filters.date !== 'any' || filters.folder != null
	);
}

/**
 * The words Outlook searches for: the typed text plus a keyword per chip. Graph refuses $filter
 * beside $search, so the chips become Outlook's own search keywords.
 */
export function composeMailSearch(text: string, filters: MailSearchFilters, now = new Date()): string {
	const parts = [text.trim()];
	if (filters.from) parts.push(`from:${filters.from.address}`);
	if (filters.attachments) parts.push('hasattachments:true');
	if (filters.unread) parts.push('isread:false');
	if (filters.date !== 'any') {
		const since = new Date(now.getTime() - DATE_DAYS[filters.date] * 86_400_000);
		parts.push(`received>=${since.toISOString().slice(0, 10)}`);
	}
	return parts.filter(Boolean).join(' ');
}
